import autobind from 'autobind-decorator';
import React from 'react';
import { classNames } from '../../utils/class-names.js';
import { BmUIKitConfig } from '../../utils/config.js';
import { FormContext } from '../Form/FormContext.js';
import { FormField } from '../FormField/FormField.js';
import { FormItemTemplateNormal } from './FormItemTemplateNormal.js';

function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class FormItem extends React.Component {
    getInitialState() {
        return {};
    }
    getEmptyValue() {
        return undefined;
    }
    componentDidMount() {
        const { readOnly = false } = this.context || {};
        this.lastContextReadonly = readOnly;
    }
    shouldFieldRevalidate(prevProps, _prevState) {
        const { readOnly: contextReadOnly } = this.context;
        const { required, disabled, readOnly } = this.props;
        if (contextReadOnly !== this.lastContextReadonly) {
            this.lastContextReadonly = contextReadOnly;
            return true;
        }
        return prevProps.required !== required || prevProps.disabled !== disabled || prevProps.readOnly !== readOnly;
    }
    componentDidUpdate(prevProps, prevState) {
        const { form, readOnly: ctxReadOnly } = this.context;
        const { name, disabled, readOnly } = this.props;
        if (disabled !== prevProps.disabled || readOnly !== prevProps.readOnly || ctxReadOnly !== this.lastContextReadonly) {
            if (disabled || readOnly || ctxReadOnly) {
                form.focus(name);
                setTimeout(()=>form.blur(name), 0);
            } else {
                const value = form.getFieldState(name)?.value;
                form.resetFieldState(name);
                form.change(name, value);
            }
        }
        if (disabled && !prevProps.disabled) {
            form.resetFieldState(name);
            form.change(name, null);
            form.change(name, undefined);
        } else if (this.shouldFieldRevalidate(prevProps, prevState)) {
            const field = form.getFieldState(name);
            if (!field) {
                return;
            }
            form.resetFieldState(name);
            form.change(name, null);
            form.change(name, field.value);
        }
    }
    componentWillUnmount() {}
    handleChange(value) {
        const { form } = this.context;
        const { onChange, name } = this.props;
        form.change(name, value);
        if (onChange) {
            onChange(value, form);
        }
    }
    handleFocus(event) {
        const { form } = this.context;
        const { onFocus, name } = this.props;
        form.focus(name);
        if (onFocus) {
            onFocus(event);
        }
    }
    handleBlur(event) {
        const { form } = this.context;
        const { onBlur, name } = this.props;
        form.blur(name);
        if (onBlur) {
            onBlur(this.parse(form.getFieldState(name).value), event);
        }
    }
    handleValidate(value, allValues) {
        const { validate } = this.props;
        if (validate) {
            return validate(value, allValues);
        }
    }
    getGroupClassName(_fieldProps) {
        const { required, className } = this.props;
        return classNames(required && "required", this.isDisabled() && "disabled", className);
    }
    getTestId() {
        const { name } = this.props;
        return name;
    }
    isDisabled() {
        const { readOnly: formReadOnly } = this.context;
        const { disabled, readOnly } = this.props;
        return disabled || readOnly || formReadOnly;
    }
    parse(value) {
        return value;
    }
    format(value) {
        return value;
    }
    renderInfo(info) {
        return info;
    }
    renderInner(fieldProps, ref) {
        const { itemTemplate } = this.context;
        const { template: Template = itemTemplate || BmUIKitConfig.getOption("defaultFormItemTemplate") || FormItemTemplateNormal, info, readOnly, disabled, infoTooltip, required } = this.props;
        const { meta: { error, valid, invalid, active, touched } } = fieldProps;
        return /*#__PURE__*/ React.createElement(Template, {
            className: this.getGroupClassName(fieldProps),
            label: this.renderLabel(fieldProps),
            field: this.renderField(fieldProps, ref),
            info: this.renderInfo(info),
            infoTooltip: infoTooltip,
            error: error,
            valid: valid,
            invalid: invalid,
            readOnly: readOnly || disabled,
            active: active,
            touched: touched,
            required: required
        });
    }
    renderLabel(_fieldProps) {
        const { label } = this.props;
        if (!label) {
            return null;
        }
        return label;
    }
    render() {
        const { name } = this.props;
        return /*#__PURE__*/ React.createElement(FormField, {
            name: name,
            validate: (value, allValues)=>{
                if (this.isDisabled()) {
                    return undefined;
                }
                return this.handleValidate(value, allValues);
            },
            parse: this.parse,
            format: this.format,
            defaultValue: this.emptyValue
        }, (fieldProps)=>this.renderInner(fieldProps, undefined));
    }
    constructor(props){
        super(props), this.lastContextReadonly = undefined;
        this.id = `${FormItem.idCounter}`;
        FormItem.idCounter += 1;
        this.state = this.getInitialState();
        this.emptyValue = this.getEmptyValue();
    }
}
FormItem.contextType = FormContext;
FormItem.idCounter = 0;
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof FieldType === "undefined" ? Object : FieldType
    ]),
    _ts_metadata("design:returntype", void 0)
], FormItem.prototype, "handleChange", null);
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof React === "undefined" || typeof React.FocusEvent === "undefined" ? Object : React.FocusEvent
    ]),
    _ts_metadata("design:returntype", void 0)
], FormItem.prototype, "handleFocus", null);
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof React === "undefined" || typeof React.FocusEvent === "undefined" ? Object : React.FocusEvent
    ]),
    _ts_metadata("design:returntype", void 0)
], FormItem.prototype, "handleBlur", null);
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof FieldType === "undefined" ? Object : FieldType,
        Object
    ]),
    _ts_metadata("design:returntype", Object)
], FormItem.prototype, "handleValidate", null);
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof InputType === "undefined" ? Object : InputType
    ]),
    _ts_metadata("design:returntype", typeof FieldType === "undefined" ? Object : FieldType)
], FormItem.prototype, "parse", null);
_ts_decorate([
    autobind,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof FieldType === "undefined" ? Object : FieldType
    ]),
    _ts_metadata("design:returntype", typeof InputType === "undefined" ? Object : InputType)
], FormItem.prototype, "format", null);
function isValidationShow(props) {
    const { readOnly, invalid, error, touched } = props;
    if (!error) {
        return false;
    }
    return !!(!readOnly && invalid && touched);
}

export { FormItem, isValidationShow };
//# sourceMappingURL=FormItem.js.map
